import React from 'react';
import { useBreakpoints } from 'hooks/useBreakpoints';

// ✅ Helper Function
function generateDynamicRanges(breaks) {
  if (!breaks || breaks.length < 2) return [];

  // Check if all breaks are integers
  const allIntegers = breaks.every(b => Number.isInteger(b));

  const ranges = [];
  for (let i = 0; i < breaks.length - 1; i++) {
    const start = breaks[i];
    const next = breaks[i + 1];
    const isLastRange = i === breaks.length - 2;
    const diff = next - start;
    let end;

    // For the last range, always use the actual max value
    if (isLastRange) {
      end = next;
    } else if (allIntegers) {
      // For integers — subtract 5% of step or at least 1
      const decrement = Math.max(Math.ceil(diff * 0.05), 1);
      end = next - decrement;
      if (end <= start) end = next - 1; // fallback safety
    } else {
      // For floats — shift about 1/3 toward the next break
      const decrement = diff * 0.33;
      end = +(start + diff - decrement).toFixed(2);
    }

    ranges.push(`${start} to ${end}`);
  }
  return ranges;
}


function MapLegend({
  colors = [],
  breaks = [],
  showPatternLayer,
  showColorLayer,
  setMapConfigData,
  patternBreaks = []
}) {
  const { breakpoints } = useBreakpoints();
  //=========== Mobile View =============
  const isMobile = breakpoints.down('md');
  const indicator_1_name = setMapConfigData?.colorIndicator?.label;
  const indicator_2_name = setMapConfigData?.patternIndicator?.label;
  const indicator_1_sub_name = setMapConfigData?.colorIndicator?.subIndicator?.label;
  const indicator_2_sub_name = setMapConfigData?.patternIndicator?.subIndicator?.label;

  const patternImages = [
    '/newpatterns/p1-2.png',
    '/patterns/p2.png',
    '/newpatterns/p3-2.png',
    '/patterns/p4.png',
    '/patterns/p5.png',
    '/patterns/p6.jpeg',
  ];

  const isValidBreaks = (arr) =>
    Array.isArray(arr) && arr.every(b => !isNaN(parseFloat(b)));

  // ✅ Use helper to generate dynamic labels
  const colorRanges = isValidBreaks(breaks) ? generateDynamicRanges(breaks) : [];
  const patternRanges = isValidBreaks(patternBreaks) ? generateDynamicRanges(patternBreaks) : [];

  return (
    <>
      {/* Pattern Layer Box */}
      {showPatternLayer && indicator_2_name ? (
        patternRanges.length > 0 ? (
          // ===== CASE 1: patternRanges > 0 =====
          <div
            className="legend-box bg-white border rounded shadow p-3 mt-2"
            style={{ flex: "1 1 auto", width: isMobile ? "calc(100% - 1rem)" : "calc(20%)", margin: "0.5rem" }}
          >
            {patternRanges.map((range, i) => (
              <div key={i} className="d-flex align-items-center mb-1">
                <div
                  className="me-2 border"
                  style={{
                    width: '1.25rem',
                    height: '1.25rem',
                    backgroundImage: `url('${patternImages[i] || patternImages[5]}')`,
                    backgroundSize: 'cover'
                  }}
                />
                {range}
              </div>
            ))}

            {/* No Data */}
            <div className="d-flex align-items-center mb-1 mt-2">
              <div
                className="me-2 border"
                style={{
                  width: '1.25rem',
                  height: '1.25rem',
                  backgroundImage: `url('${patternImages[5]}')`,
                  backgroundSize: 'cover'
                }}
              />
              Data not available
            </div>

            <div className="fw-bold mt-2">
              {indicator_2_name}
              {indicator_2_sub_name && (
                <span className="text-success"> | [{indicator_2_sub_name}]</span>
              )}
            </div>
          </div>
        ) : (
          // ===== CASE 2: patternRanges === 0 =====
          <div
            className="legend-box bg-white border rounded shadow p-3 mt-2"
            style={{ flex: "1 1 auto", width: isMobile ? "calc(100% - 1rem)" : "calc(20%)", margin: "0.5rem" }}
          >
            <div className="d-flex align-items-center mb-1">
              No data available
            </div>

            <div className="fw-bold mt-2">
              {indicator_2_name}
              {indicator_2_sub_name && (
                <span className="text-success"> | [{indicator_2_sub_name}]</span>
              )}
            </div>
          </div>
        )
      ) : null}


      {/* Color Layer Box */}
      {showColorLayer && indicator_1_name ? (
        colorRanges.length > 0 && colors.length > 0 ? (
          // ===== CASE 1: colorRanges exist =====
          <div
            className="legend-box bg-white border rounded shadow p-3 mt-2"
            style={{ flex: "1 1 auto", width: isMobile ? "calc(100% - 1rem)" : "calc(20%)", margin: "0.5rem" }}
          >
            {colorRanges.map((range, i) => {
              const colorIndex = i + 1 > colors.length - 1 ? colors.length - 1 : i + 1;
              return (
                <div key={i} className="d-flex align-items-center mb-1">
                  <div
                    className="me-2 border"
                    style={{
                      width: '1.25rem',
                      height: '1.25rem',
                      background: colors[colorIndex]
                    }}
                  />
                  {range}
                </div>
              );
            })}

            {/* No Data */}
            <div className="d-flex align-items-center mb-1 mt-2">
              <div
                className="me-2 border"
                style={{
                  width: '1.25rem',
                  height: '1.25rem',
                  background: '#ffffff'
                }}
              />
              Data not available
            </div>

            <div className="fw-bold mt-2">
              {indicator_1_name}
              {indicator_1_sub_name && (
                <span className="text-success"> | [{indicator_1_sub_name}]</span>
              )}
            </div>
          </div>
        ) : (
          // ===== CASE 2: No colorRanges =====
          <div
            className="legend-box bg-white border rounded shadow p-3 mt-2"
            style={{ flex: "1 1 auto", width: isMobile ? "calc(100% - 1rem)" : "calc(20%)", margin: "0.5rem" }}
          >
            <div className="d-flex align-items-center mb-1">
              <div
                className="me-2 border"
                style={{
                  width: '1.25rem',
                  height: '1.25rem',
                  background: '#ffffff'
                }}
              />
              No data available
            </div>

            <div className="fw-bold mt-2">
              {indicator_1_name}
              {indicator_1_sub_name && (
                <span className="text-success"> | [{indicator_1_sub_name}]</span>
              )}
            </div>
          </div>
        )
      ) : null}

    </>
  );
}

export default MapLegend;
